<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { CALENDLY_EVENT_SCHEDULED, CALENDLY_URL_REGEX } from './_utils/constants';
    import { DEFAULT_LAYOUT } from '$lib/components/constants';
    import { enqueueCalendlyScripts } from './_utils/calendly';
    import Form from './FormQuestion/FormQuestion.svelte';
    import get from 'lodash/get';
    import isEmpty from 'lodash/isEmpty';

    // Types
    import type { RawDraftContentState } from 'draft-js';
    import type { FormValues, Input } from '$lib/types/Form';
    import type { Box as BoxType, Align, Size } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';

    // Calendly
    export let calendlyUrl = '';
    export let onCalendlyFailed = () => console.error('Calendly could not be loaded...');
    export let onCalendlyFormSubmit: (formValues: FormValues) => void = () => null;

    // Question
    export let align: Align = 'center';
    export let draft: RawDraftContentState;
    export let color = 'black';
    export let box: BoxType;
    export let onSubmit: (formValues: FormValues) => void = () => null;
    export let onMouseEnter = () => null;
    export let layout = DEFAULT_LAYOUT;
    export let language: Language = 'de';

    // Inputs
    export let inputs: InputData[] = [];

    // Submit Button
    export let btnId = '';
    export let btnDraft: RawDraftContentState;
    export let btnBgColor = '#0076FF';
    export let btnColor = 'white';
    export let btnAlign: Align = 'center';
    export let btnSize: Size = 'medium';
    export let loading = false;

    let savedFormValues: FormValues = {};

    const validateUrl = (url: string) => {
        return CALENDLY_URL_REGEX.test(url);
    };

    const isValidUrl = validateUrl(calendlyUrl);

    onMount(() => {
        window.addEventListener('message', handleMessageEvent);
        enqueueCalendlyScripts();
    });

    onDestroy(() => {
        window.removeEventListener('message', handleMessageEvent);

        if (!isEmpty(window.Calendly)) {
            window.Calendly.closePopupWidget();
        }
    });

    const extractCustomAnswers = (inputsParam: Input[], formData: FormValues) => {
        let counter = 1;
        const customAnswers: FormValues = {};

        inputsParam.forEach((input) => {
            const { name } = input;

            if (name === 'name' || name === 'email') {
                return;
            }

            const value = get(formData, name, '');

            if (typeof value === 'boolean') {
                return;
            }

            customAnswers[`a${counter}`] = get(formData, name, '');
            counter += 1;
        });

        return customAnswers;
    };

    const openCalendlyPopup = (formData: FormValues) => {
        const Calendly = window.Calendly;

        if (isEmpty(Calendly)) {
            onCalendlyFailed();
            return;
        }

        const name = formData.name;
        const email = formData.email;
        const phone = formData.phone;

        if (phone) {
            // PER-2085: Prefill does currently not directly support these fields, so they are added to the url
            calendlyUrl = calendlyUrl + '?location=' + phone + '&phone_number=' + phone;
        }
        const customAnswers = extractCustomAnswers(inputs, formData);

        Calendly.initPopupWidget({
            url: calendlyUrl,
            prefill: {
                name,
                email,
                customAnswers,
            },
        });
    };

    const handleEventScheduled = () => {
        window.Calendly.closePopupWidget();

        onSubmit(savedFormValues);
    };

    const handleMessageEvent = (event: MessageEvent) => {
        const eventName = get(event, 'data.event', '');
        const isCalendlyEvent = eventName.includes('calendly');

        if (isCalendlyEvent && eventName === CALENDLY_EVENT_SCHEDULED) {
            handleEventScheduled();
        }
    };

    const handleSubmit = (formData: FormValues) => {
        onCalendlyFormSubmit(formData);

        if (isValidUrl) {
            savedFormValues = formData;

            return openCalendlyPopup(formData);
        }

        return console.warn('Invalid Calendly URL!');
    };
</script>

<Form
    {align}
    {draft}
    {color}
    {box}
    {inputs}
    {btnId}
    {btnDraft}
    {btnBgColor}
    {btnColor}
    {btnAlign}
    {btnSize}
    {loading}
    {layout}
    {language}
    {onMouseEnter}
    onSubmit={handleSubmit}
/>
